import { registerSW } from 'virtual:pwa-register';
import { notify } from '@/presentation/utils';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const UPDATE_RELOAD_FALLBACK_MS = 4000;

let isApplyingUpdate = false;
let isUpdatePromptVisible = false;

async function updatePage(updateSW: (reloadPage?: boolean) => Promise<void>) {
	if (isApplyingUpdate) return;
	isApplyingUpdate = true;

	let reloadTimer: number | undefined;
	const reloadPage = () => window.location.reload();

	if ('serviceWorker' in navigator) {
		navigator.serviceWorker.addEventListener('controllerchange', reloadPage, { once: true });
		reloadTimer = window.setTimeout(reloadPage, UPDATE_RELOAD_FALLBACK_MS);
	}

	try {
		await updateSW(true);
	} catch (error) {
		if (reloadTimer) window.clearTimeout(reloadTimer);
		navigator.serviceWorker?.removeEventListener('controllerchange', reloadPage);
		isApplyingUpdate = false;
		throw error;
	}
}

function showUpdatePrompt(updateSW: (reloadPage?: boolean) => Promise<void>) {
	if (isUpdatePromptVisible) return;

	isUpdatePromptVisible = true;

	notify({
		message: 'بروزرسانی جدیدی از این صفحه در دسترس است.',
		duration: 0,
		type: 'info',
		replaceExisting: true,
		actions: [
			{
				label: 'بروزرسانی',
				callback: async () => {
					try {
						await updatePage(updateSW);
					} catch (_error) {
						isUpdatePromptVisible = false;
						notify({
							message: 'خطا در بروزرسانی نسخه جدید. لطفا صفحه را دستی رفرش کنید.',
							type: 'error',
							duration: 7000,
							showCloseButton: true,
							replaceExisting: true,
						});
					}
				},
			},
		],
	});
}

function setupPeriodicServiceWorkerUpdate(swScriptUrl: string, registration?: ServiceWorkerRegistration) {
	if (!registration) return;

	setInterval(async () => {
		try {
			if (registration.installing || !navigator.onLine) return;

			const response = await fetch(swScriptUrl, {
				cache: 'no-store',
				headers: {
					cache: 'no-store',
					'cache-control': 'no-cache',
				},
			});

			if (response?.status === 200) {
				await registration.update();
			}
		} catch (error) {
			console.warn('Unable to check service worker update.', error);
		}
	}, UPDATE_CHECK_INTERVAL_MS);
}

function setupPwaUpdater() {
	const updateSW = registerSW({
		immediate: true,
		onNeedRefresh() {
			showUpdatePrompt(updateSW);
		},
		onOfflineReady() {
			// App shell is available offline.
		},
		onRegisteredSW(swScriptUrl, registration) {
			setupPeriodicServiceWorkerUpdate(swScriptUrl, registration);
		},
		onRegisterError(error) {
			console.warn('Service worker registration failed.', error);
		},
	});
}

export default setupPwaUpdater;
